import axios from 'axios';
import AuthService from './auth-header';

let API_URL = ''
    if (process.env.NODE_ENV === "production") {
        API_URL = 'https://ats.sattvahuman.in/api/'
    } else {
        API_URL = 'http://192.168.0.30:5000/api/'
    }

class EnhancedInvoiceRevisionService {
    // Initiate a revision for an invoice
    async initiateRevision(invoiceId, revisionData) {
        try {
            const res = await axios.post(API_URL + 'invoice/' + invoiceId + '/revision/initiate', revisionData, {
                headers: AuthService.authHeader()
            });
            return res.data;
        } catch (err) {
            console.log(err.response);
            return err.response;
        }
    }

    // Get revision history for an invoice
    async getRevisionHistory(invoiceId) {
        try {
            const res = await axios.get(API_URL + 'invoice/' + invoiceId + '/revision/history', {
                headers: AuthService.authHeader(),
                // Soft-fail missing history route so revise UI never hard-errors.
                validateStatus: (status) => (status >= 200 && status < 300)
                    || status === 404 || status === 405 || status === 501,
            });
            if (res.status === 404 || res.status === 405 || res.status === 501) {
                // Empty OK + _source — callers must surface "history API missing", not invent rows.
                if (typeof console !== 'undefined' && console.warn) {
                    console.warn(
                        `[invoice-revision] GET invoice/${invoiceId}/revision/history → ${res.status} (no history route)`
                    )
                }
                return { status: 'OK', data: [], _source: 'missing-route' };
            }
            const body = res.data && typeof res.data === 'object' ? res.data : { status: 'OK', data: res.data }
            if (!body._source) body._source = 'revision-history-api'
            return body;
        } catch (err) {
            const code = err && err.response && err.response.status;
            if (code === 404 || code === 405 || code === 501) {
                if (typeof console !== 'undefined' && console.warn) {
                    console.warn(
                        `[invoice-revision] GET invoice/${invoiceId}/revision/history → ${code} (no history route)`
                    )
                }
                return { status: 'OK', data: [], _source: 'missing-route' };
            }
            return (err.response && err.response.data) || { status: 'ERROR', data: [], _source: 'error' };
        }
    }

    /**
     * Soft-map REAL Postgres `invoice_revisions` columns (and legacy aliases)
     * onto the snake_case fields timeline + versions UI already read.
     * Never invents rows or values.
     *
     * Source-of-truth columns:
     *   id, invoice_id, revision_type, change_type, old_amount, new_amount,
     *   note_type, note_date, r1_filed, status, document_urls,
     *   initiated_by, approved_by, createdAt, updatedAt
     */
    normalizeRevisionRow(row, indexHint = null) {
        if (!row || typeof row !== 'object') return row
        const pick = (...keys) => {
            for (const k of keys) {
                if (row[k] !== undefined && row[k] !== null && row[k] !== '') return row[k]
            }
            return undefined
        }
        const out = { ...row }
        const setIfMissing = (snake, value) => {
            if (value === undefined) return
            if (out[snake] === undefined || out[snake] === null || out[snake] === '') {
                out[snake] = value
            }
        }

        setIfMissing('id', pick('id', 'revision_id', 'revisionId'))
        setIfMissing('invoice_id', pick('invoice_id', 'invoiceId', 'invoice_number'))
        setIfMissing('revision_type', pick('revision_type', 'revisionType'))
        // Real DB: change_type; legacy: amount_change_type
        setIfMissing(
            'change_type',
            pick('change_type', 'changeType', 'amount_change_type', 'amountChangeType'),
        )
        setIfMissing(
            'amount_change_type',
            pick('amount_change_type', 'amountChangeType', 'change_type', 'changeType'),
        )
        setIfMissing('old_amount', pick('old_amount', 'oldAmount'))
        setIfMissing('new_amount', pick('new_amount', 'newAmount'))
        // Prefer new_amount as the "total at revision" display amount when total_amount absent
        setIfMissing(
            'total_amount',
            pick('total_amount', 'totalAmount', 'new_amount', 'newAmount'),
        )
        setIfMissing('note_type', pick('note_type', 'noteType'))
        setIfMissing('note_date', pick('note_date', 'noteDate'))
        setIfMissing('r1_filed', pick('r1_filed', 'r1Filed'))
        setIfMissing('status', pick('status', 'status_after', 'statusAfter'))
        setIfMissing('status_after', pick('status_after', 'statusAfter', 'status'))
        setIfMissing('amend_invoice', pick('amend_invoice', 'amendInvoice'))
        setIfMissing('revision_reason', pick('revision_reason', 'revisionReason', 'reason'))
        setIfMissing('comment', pick('comment', 'revision_comment', 'revisionComment'))
        setIfMissing('note_sent_to_client', pick('note_sent_to_client', 'noteSentToClient'))
        setIfMissing('note_einvoice_done', pick('note_einvoice_done', 'noteEinvoiceDone', 'noteEInvoiceDone'))
        setIfMissing('status_before', pick('status_before', 'statusBefore'))
        setIfMissing('base_amount', pick('base_amount', 'baseAmount'))
        setIfMissing('gst_amount', pick('gst_amount', 'gstAmount', 'gst'))
        setIfMissing('amount_with_gst', pick('amount_with_gst', 'amountWithGst', 'amountWithGST'))

        // Actors: real DB uses initiated_by / approved_by
        setIfMissing(
            'initiated_by',
            pick('initiated_by', 'initiatedBy', 'created_by', 'createdBy'),
        )
        setIfMissing('approved_by', pick('approved_by', 'approvedBy'))
        setIfMissing(
            'created_by',
            pick(
                'created_by',
                'createdBy',
                'initiated_by',
                'initiatedBy',
                'approved_by',
                'approvedBy',
                'updated_by',
                'updatedBy',
            ),
        )

        // Timestamps: Sequelize-style createdAt / updatedAt on real table
        setIfMissing(
            'created_at',
            pick('created_at', 'createdAt', 'updated_at', 'updatedAt'),
        )
        setIfMissing('updated_at', pick('updated_at', 'updatedAt'))
        setIfMissing('createdAt', pick('createdAt', 'created_at'))
        setIfMissing('updatedAt', pick('updatedAt', 'updated_at'))

        // document_urls jsonb → flat path aliases used by versions/timeline
        this.applyDocumentUrls(out)

        setIfMissing(
            'revised_invoice_file_path',
            pick('revised_invoice_file_path', 'revisedInvoiceFilePath', 'revised_file', 'revisedFile'),
        )
        setIfMissing(
            'original_invoice_file_path',
            pick(
                'original_invoice_file_path',
                'originalInvoiceFilePath',
                'previous_pdf',
                'previousPdf',
                'old_file',
                'oldFile',
                'old_pdf',
                'previous_file_path',
            ),
        )
        setIfMissing('r1_upload_file_path', pick('r1_upload_file_path', 'r1UploadFilePath', 'r1_file'))
        setIfMissing('note_file_path', pick('note_file_path', 'noteFilePath', 'note_file'))

        // Derive display revision_number when column absent (real table has no revision_number)
        const existingNum = pick('revision_number', 'revisionNumber', 'rev_number', 'version')
        if (existingNum !== undefined) {
            setIfMissing('revision_number', existingNum)
        } else if (indexHint != null && Number.isFinite(Number(indexHint))) {
            setIfMissing('revision_number', Number(indexHint) + 1)
        } else if (out.id != null && out.id !== '') {
            // Last resort: use id so UI still shows a stable "#N" rather than blank
            setIfMissing('revision_number', out.id)
        }

        return out
    }

    /**
     * Map document_urls jsonb keys onto revised/original/r1/note path fields.
     * Accepts object or JSON string; never invents paths.
     */
    applyDocumentUrls(out) {
        if (!out || typeof out !== 'object') return
        let urls = out.document_urls
        if (urls == null) urls = out.documentUrls
        if (typeof urls === 'string') {
            try {
                urls = JSON.parse(urls)
            } catch (_) {
                urls = null
            }
        }
        if (!urls || typeof urls !== 'object' || Array.isArray(urls)) return

        const pickUrl = (...keys) => {
            for (const k of keys) {
                const v = urls[k]
                if (typeof v === 'string' && v.trim()) return v.trim()
            }
            return undefined
        }
        const setIfMissing = (snake, value) => {
            if (value === undefined) return
            if (out[snake] === undefined || out[snake] === null || out[snake] === '') {
                out[snake] = value
            }
        }

        setIfMissing(
            'revised_invoice_file_path',
            pickUrl(
                'revised_invoice_file_path',
                'revised_invoice_url',
                'revised_invoice',
                'revised',
                'current',
                'current_pdf',
                'new',
            ),
        )
        setIfMissing(
            'original_invoice_file_path',
            pickUrl(
                'original_invoice_file_path',
                'original_invoice_url',
                'previous_pdf',
                'old_file',
                'old_pdf',
                'original',
                'previous',
            ),
        )
        setIfMissing(
            'r1_upload_file_path',
            pickUrl('r1_upload_file_path', 'r1_upload_url', 'r1', 'r1_file'),
        )
        setIfMissing(
            'note_file_path',
            pickUrl('note_file_path', 'note_file_url', 'note', 'debit_note', 'credit_note'),
        )

        // Keep a files{} mirror for revisionDocumentLinks
        if (!out.files || typeof out.files !== 'object') {
            out.files = {}
        }
        const files = out.files
        const setFile = (key, value) => {
            if (value && (files[key] === undefined || files[key] === null || files[key] === '')) {
                files[key] = value
            }
        }
        setFile('revised_invoice_url', pickUrl('revised_invoice_url', 'revised_invoice', 'revised', 'current'))
        setFile('original_invoice_url', pickUrl('original_invoice_url', 'previous_pdf', 'original', 'old_file'))
        setFile('previous_pdf_url', pickUrl('previous_pdf', 'previous_pdf_url', 'old_file'))
        setFile('r1_upload_url', pickUrl('r1_upload_url', 'r1', 'r1_file'))
        setFile('note_file_url', pickUrl('note_file_url', 'note', 'debit_note', 'credit_note'))
    }

    /**
     * Normalize revision-history API body to a flat array + source flag.
     * Does not invent rows — empty means empty.
     * Derives revision_number from chronological index when column is absent.
     */
    extractRevisionRows(res) {
        if (!res) return { rows: [], source: 'empty' }
        const source = res._source || 'revision-history-api'
        const ok = res.status === 'OK' || res.status === 200
            || (typeof res.status === 'string' && String(res.status).toLowerCase() === 'ok')
        if (!ok && !Array.isArray(res.data) && !Array.isArray(res)) {
            return { rows: [], source: source === 'missing-route' ? source : 'error' }
        }
        let raw = null
        if (Array.isArray(res)) raw = res
        else if (Array.isArray(res.data)) raw = res.data
        else if (res.data && Array.isArray(res.data.revisions)) raw = res.data.revisions
        else if (res.data && Array.isArray(res.data.history)) raw = res.data.history
        else if (Array.isArray(res.revisions)) raw = res.revisions
        else if (Array.isArray(res.history)) raw = res.history
        if (!raw) return { rows: [], source }

        // Sort oldest→newest so index-based revision_number is stable
        const sorted = [...raw].sort((a, b) => {
            const ta = Date.parse((a && (a.createdAt || a.created_at || a.updatedAt || a.updated_at)) || '') || 0
            const tb = Date.parse((b && (b.createdAt || b.created_at || b.updatedAt || b.updated_at)) || '') || 0
            if (ta !== tb) return ta - tb
            const ida = Number(a && a.id) || 0
            const idb = Number(b && b.id) || 0
            return ida - idb
        })

        const hasExplicitNumber = sorted.some((r) => (
            r
            && r.revision_number != null
            && r.revision_number !== ''
        ) || (
            r
            && r.revisionNumber != null
            && r.revisionNumber !== ''
        ))

        const rows = sorted.map((r, idx) => {
            // Only pass index hint when no row in the list has an explicit revision_number
            const hint = hasExplicitNumber ? null : idx
            return this.normalizeRevisionRow(r, hint)
        })
        return { rows, source }
    }

    // Get a specific revision by ID
    async getRevisionById(invoiceId, revisionId) {
        try {
            const res = await axios.get(API_URL + 'invoice/' + invoiceId + '/revision/' + revisionId, {
                headers: AuthService.authHeader()
            });
            return res.data;
        } catch (err) {
            console.log(err.response);
            return err.response;
        }
    }

    // Upload revision document
    async uploadRevisionDocument(invoiceId, revisionId, documentType, file) {
        try {
            const formData = new FormData();
            formData.append('document', file);
            formData.append('documentType', documentType);
            formData.append('revisionId', revisionId);
            const res = await axios.post(API_URL + 'invoice/' + invoiceId + '/revision/' + revisionId + '/document', formData, {
                headers: AuthService.authHeaderForm()
            });
            return res.data;
        } catch (err) {
            console.log(err.response);
            return err.response;
        }
    }

    // Update revision status
    async updateRevisionStatus(invoiceId, revisionId, status) {
        try {
            const data = { status };
            const res = await axios.put(API_URL + 'invoice/' + invoiceId + '/revision/' + revisionId + '/status', data, {
                headers: AuthService.authHeader()
            });
            return res.data;
        } catch (err) {
            console.log(err.response);
            return err.response;
        }
    }

    // Approve revision
    async approveRevision(invoiceId, revisionId, approvalData) {
        try {
            const res = await axios.post(API_URL + 'invoice/' + invoiceId + '/revision/' + revisionId + '/approve', approvalData, {
                headers: AuthService.authHeader()
            });
            return res.data;
        } catch (err) {
            console.log(err.response);
            return err.response;
        }
    }

    // Reject revision
    async rejectRevision(invoiceId, revisionId, rejectionData) {
        try {
            const res = await axios.post(API_URL + 'invoice/' + invoiceId + '/revision/' + revisionId + '/reject', rejectionData, {
                headers: AuthService.authHeader()
            });
            return res.data;
        } catch (err) {
            console.log(err.response);
            return err.response;
        }
    }

    // Get all pending revisions
    async getPendingRevisions() {
        try {
            const res = await axios.get(API_URL + 'invoice/revision/pending', {
                headers: AuthService.authHeader()
            });
            return res.data;
        } catch (err) {
            console.log(err.response);
            return err.response;
        }
    }

    // Delete a revision
    async deleteRevision(invoiceId, revisionId) {
        try {
            const res = await axios.delete(API_URL + 'invoice/' + invoiceId + '/revision/' + revisionId, {
                headers: AuthService.authHeader()
            });
            return res.data;
        } catch (err) {
            console.log(err.response);
            return err.response;
        }
    }
}

export default new EnhancedInvoiceRevisionService();
